import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useAdminGetLoginConfig, useAdminGoogleLogin } from '@/api/generated/v2-admin/v2-admin'
import type { AdminLoginConfigResponse, AdminTokenResponse } from '@/api/schemas'
import { adminAuth } from '@/lib/adminApi'
import { loadGoogleIdentity } from '@/lib/googleIdentity'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

export const Route = createFileRoute('/admin/login')({
  component: AdminLogin,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const redirect = search.redirect
    // 내부 어드민 경로만 복귀 허용 (open-redirect 방지) + 로그인 페이지 되돌이 배제
    if (
      typeof redirect === 'string' &&
      redirect.startsWith('/admin') &&
      !redirect.startsWith('/admin/login')
    ) {
      return { redirect }
    }
    return {}
  },
})

/** 서버 거부 사유(status) → 안내 문구. 서버 code 는 ADMIN_GOOGLE_TOKEN_INVALID / ADMIN_NOT_ALLOWED / ADMIN_GOOGLE_LOGIN_UNAVAILABLE */
function googleLoginErrorMessage(error: unknown): string {
  const status = (error as { status?: number } | null)?.status
  if (status === 403) {
    return '어드민 접근이 허용되지 않은 계정이에요. 허용 목록 등록을 요청해주세요.'
  }
  if (status === 401) {
    return 'Google 로그인 정보를 확인하지 못했어요. 회사 계정(@inwv.co)으로 다시 시도해주세요.'
  }
  if (status === 503) {
    return '지금은 Google 로그인을 쓸 수 없어요. 잠시 후 다시 시도해주세요.'
  }
  return '로그인에 실패했어요. 잠시 후 다시 시도해주세요.'
}

function AdminLogin() {
  const navigate = useNavigate()
  const router = useRouter()
  const { redirect } = Route.useSearch()
  const [error, setError] = useState<string | null>(null)

  // 로그인 성공 공통 처리 — 토큰 저장 후 딥링크(validateSearch 가 내부 경로만 통과시킴) 또는 고객사 관리로
  const handleToken = (response: unknown) => {
    // custom-instance가 응답 body를 직접 반환하므로 response가 곧 데이터
    const data = response as AdminTokenResponse
    adminAuth.setToken(data.access_token)
    if (redirect) {
      // 딥링크는 쿼리(`?intent=…` 등)까지 실린 URL 문자열이다. navigate({ to }) 는 `to` 를
      // 경로로만 해석해 쿼리가 경로 조각으로 섞이므로, history 로 URL 을 그대로 바꿔 끼운다
      // (라우터가 history 를 구독해 search 를 다시 파싱한다)
      router.history.replace(redirect)
      return
    }
    navigate({ to: '/admin/customers', replace: true })
  }

  // Google 클라이언트 ID 는 서버에서 받는다 — 서버의 aud 검증 값과 한 곳에서 맞물리게
  const configQuery = useAdminGetLoginConfig()
  const config = configQuery.data as AdminLoginConfigResponse | undefined

  const googleLogin = useAdminGoogleLogin({
    mutation: {
      onSuccess: handleToken,
      onError: (err) => setError(googleLoginErrorMessage(err)),
    },
  })

  // GIS 콜백은 initialize 시점에 한 번 묶이므로, 최신 mutate 를 ref 로 넘긴다
  const googleMutateRef = useRef(googleLogin.mutate)
  googleMutateRef.current = googleLogin.mutate

  const buttonRef = useRef<HTMLDivElement>(null)
  const [gisError, setGisError] = useState<string | null>(null)
  const clientId = config?.google_client_id ?? null
  const hostedDomain = config?.hosted_domain

  useEffect(() => {
    if (!clientId || !buttonRef.current) return
    let cancelled = false
    loadGoogleIdentity()
      .then((gis) => {
        if (cancelled || !buttonRef.current) return
        gis.initialize({
          client_id: clientId,
          callback: (response) => {
            setError(null)
            googleMutateRef.current({ data: { credential: response.credential } })
          },
          hd: hostedDomain,
          ux_mode: 'popup',
          context: 'signin',
          auto_select: false,
          itp_support: true,
        })
        gis.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 320,
          locale: 'ko',
        })
      })
      .catch((e: Error) => {
        if (!cancelled) setGisError(e.message)
      })
    return () => {
      cancelled = true
    }
  }, [clientId, hostedDomain])

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <Card className="p-6">
          <div className="text-center mb-6">
            <h1 className="text-xl font-bold text-on-surface">어드민 로그인</h1>
            <p className="text-on-surface-variant mt-2">자사몰 사관학교 관리자 페이지</p>
          </div>

          <div className="space-y-4">
            {configQuery.isLoading ? (
              <p className="text-center text-sm text-on-surface-variant">불러오는 중...</p>
            ) : configQuery.isError ? (
              // 설정 조회 실패 — 로그인 경로가 Google 하나뿐이라 여기서 다시 시도할 수 있어야 한다
              <div className="flex flex-col items-center gap-3">
                <p className="text-center text-sm text-on-surface-variant">
                  로그인 설정을 불러오지 못했어요. 잠시 후 다시 시도해주세요.
                </p>
                <Button
                  variant="outline"
                  onClick={() => configQuery.refetch()}
                  disabled={configQuery.isFetching}
                >
                  {configQuery.isFetching ? '다시 불러오는 중...' : '다시 시도'}
                </Button>
              </div>
            ) : clientId ? (
              <div className="flex flex-col items-center gap-2">
                {/* GIS 가 이 자리에 "Google 계정으로 로그인" 버튼을 그린다 */}
                <div ref={buttonRef} className="min-h-[44px]" />
                <p className="text-xs text-on-surface-variant">
                  회사 Google 계정(@{hostedDomain})으로 로그인해요
                </p>
                {googleLogin.isPending && (
                  <p className="text-sm text-on-surface-variant">로그인 중...</p>
                )}
              </div>
            ) : (
              <p className="text-center text-sm text-on-surface-variant">
                Google 로그인이 아직 설정되지 않았어요. 개발팀에 서버의 ADMIN_GOOGLE_CLIENT_ID 설정을
                요청해주세요.
              </p>
            )}

            {(error || gisError) && (
              <div className="text-error text-sm bg-error/10 p-3 rounded-md">
                {error ?? gisError}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
